import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = { duration: 500 };

/**
 * Robust MarqueeComponent
 * - defensive copy counts
 * - waits for images/videos to load before sizing
 * - intersection/visibility handling
 * - iOS rAF fallback
 * - scroll-end bottom restart
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  #io = null;
  #mediaObserver = null;
  #animation = null;
  #rafRunning = false;
  #rafId = null;
  #scrollTimer = null;

  connectedCallback() {
    super.connectedCallback();
    const { marqueeItems } = this.refs;
    if (!marqueeItems || marqueeItems.length === 0) return;

    // prefer eager for marquee images (best-effort)
    try {
      this.refs.content.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });
    } catch (e) {}

    // wait for media then init sizing + animation
    this.#waitForMediaLoad().then(() => {
      this.#addRepeatedItems();
      this.#duplicateContent();
      this.#setSpeed();
      this.#restartAnimation();
    });

    window.addEventListener('resize', this.#handleResize);
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);

    if ('IntersectionObserver' in window) {
      this.#io = new IntersectionObserver(this.#handleIntersection, { threshold: 0.01 });
      this.#io.observe(this.refs.wrapper);
    }

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // MutationObserver: re-run sizing when src attributes change
    this.#mediaObserver = new MutationObserver(muts => {
      const relevant = muts.some(m => (m.type === 'attributes' && m.attributeName === 'src') || m.addedNodes.length);
      if (relevant) {
        this.#waitForMediaLoad().then(() => { this.#handleResize(); this.#restartAnimation(); });
      }
    });
    this.#mediaObserver.observe(this.refs.content, { subtree: true, childList: true, attributes: true, attributeFilter: ['src'] });

    // If mobile/iOS where CSS often pauses during scroll, start RAF fallback
    if (this.#shouldUseRAFFallback()) {
      this.refs.wrapper.style.animation = 'none';
      this.#startRAF();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    window.removeEventListener('scroll', this.#handleScroll);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    if (this.#io) { this.#io.disconnect(); this.#io = null; }
    if (this.#mediaObserver) { this.#mediaObserver.disconnect(); this.#mediaObserver = null; }

    this.#stopRAF();
  }

  // animateValue helper (same as before)
  #slowDown = debounce(() => {
    if (this.#animation) return;
    const animation = this.refs.wrapper.getAnimations ? this.refs.wrapper.getAnimations()[0] : null;
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (v) => { try { animation.playbackRate = v; } catch (e) {} },
      onComplete: () => { this.#animation = null; },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    this.#slowDown.cancel();
    const animation = this.refs.wrapper.getAnimations ? this.refs.wrapper.getAnimations()[0] : null;
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (v) => { try { animation.playbackRate = v; } catch (e) {} },
      onComplete: () => { this.#animation = null; },
    });
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth || 0;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 0;
    if (marqueeRepeatedItemWidth <= 0 || marqueeWidth <= 0) return Math.max(1, Math.sqrt(2) * speedFactor);
    const count = Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
    return Math.sqrt(Math.max(2, count)) * speedFactor;
  }

  #handleResize = debounce(() => {
    // recalc copies defensively
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = this.refs.marqueeItems.length;
    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }
    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #handleScroll = debounce(() => {
    // on scroll end, re-wait for media then recalc
    this.#waitForMediaLoad().then(() => this.#handleResize());
    // detect bottom and restart (double-run to be safe)
    const atBottom = (window.scrollY + window.innerHeight) >= (document.documentElement.scrollHeight - 2);
    if (atBottom) {
      this.#restartAnimation();
      setTimeout(() => this.#restartAnimation(), 120);
    }
  }, 180);

  #restartAnimation() {
    // full reset: stop RAF, clear transform, cancel Web Animations and restart
    this.#stopRAF();
    const wrapper = this.refs.wrapper;
    if (!wrapper) return;

    wrapper.style.transform = '';
    void wrapper.offsetHeight;

    const animations = wrapper.getAnimations ? wrapper.getAnimations() : [];
    requestAnimationFrame(() => {
      for (const anim of animations) {
        try { anim.cancel(); anim.currentTime = 0; anim.playbackRate = 1; anim.play(); } catch (e) {}
      }
      if (this.#shouldUseRAFFallback()) this.#startRAF();
    });
  }

  #duplicateContent() {
    try { this.clonedContent?.remove(); } catch (e) {}
    const clone = this.refs.content.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.refs.wrapper.appendChild(clone);
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content, marqueeItems } = this.refs;
    if (!marqueeItems[0]) return;
    const existing = content.children.length || 0;
    const toAdd = Math.max(0, numberOfCopies - existing);
    for (let i = 0; i < toAdd; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const current = content.children.length || 0;
    const toRemove = Math.max(0, current - numberOfCopies);
    for (let i = 0; i < toRemove; i++) content.lastElementChild?.remove();
  }

  #calculateNumberOfCopies() {
    const { marqueeItems } = this.refs;
    const marqueeWidth = this.offsetWidth || 0;
    const marqueeRepeatedItemWidth = marqueeItems[0]?.offsetWidth ?? 0;
    if (marqueeRepeatedItemWidth <= 0 || marqueeWidth <= 0) return 2;
    return Math.max(2, Math.ceil(marqueeWidth / marqueeRepeatedItemWidth));
  }

  // IntersectionObserver callback - pause when out of view, resume when in view
  #handleIntersection = (entries) => {
    for (const entry of entries) {
      const wrapper = entry.target;
      const animations = wrapper.getAnimations ? wrapper.getAnimations() : [];
      if (entry.isIntersecting) {
        for (const a of animations) { try { a.playbackRate = 1; if (a.playState === 'paused') a.play(); } catch (e) {} }
        if (this.#rafRunning === true && !this.#rafId) this.#startRAF();
      } else {
        for (const a of animations) { try { a.pause(); } catch (e) {} }
        if (this.#rafRunning) this.#stopRAF();
      }
    }
  };

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.refs.wrapper.getAnimations?.().forEach(a => { try { a.pause(); } catch (e) {} });
      this.#stopRAF();
    } else {
      this.refs.wrapper.getAnimations?.().forEach(a => { try { a.play(); } catch (e) {} });
      if (this.#shouldUseRAFFallback()) this.#startRAF();
    }
  };

  // Wait for images/videos inside content to finish loading (short timeout)
  #waitForMediaLoad(timeout = 3000) {
    const content = this.refs?.content;
    if (!content) return Promise.resolve();
    const imgs = Array.from(content.querySelectorAll('img'));
    const vids = Array.from(content.querySelectorAll('video'));
    const promises = [];

    imgs.forEach(img => {
      if (img.complete && img.naturalWidth !== 0) return;
      promises.push(new Promise(resolve => {
        const onEnd = () => { cleanup(); resolve(); };
        const cleanup = () => { img.removeEventListener('load', onEnd); img.removeEventListener('error', onEnd); };
        img.addEventListener('load', onEnd);
        img.addEventListener('error', onEnd);
      }));
    });

    vids.forEach(v => {
      if (v.readyState >= 1) return;
      promises.push(new Promise(resolve => {
        const onMeta = () => { v.removeEventListener('loadedmetadata', onMeta); resolve(); };
        v.addEventListener('loadedmetadata', onMeta);
      }));
    });

    if (!promises.length) return Promise.resolve();
    return Promise.race([ Promise.all(promises), new Promise(r => setTimeout(r, timeout)) ]);
  }

  // rAF fallback for iOS where CSS animations may be paused during scroll
  #startRAF() {
    if (this.#rafRunning) return;
    this.#rafRunning = true;

    const wrapper = this.refs.wrapper;
    let contentWidth = wrapper.scrollWidth || this.offsetWidth || 0;
    if (contentWidth <= 0) contentWidth = Math.max(1, this.offsetWidth || 100);

    const speedSeconds = this.#calculateSpeed() || 3;
    const pxPerMs = contentWidth / (speedSeconds * 1000 || 1);
    let pos = 0;
    let last = performance.now();

    const step = (now) => {
      if (!this.#rafRunning) return;
      const dt = now - last;
      last = now;
      pos -= pxPerMs * dt;
      if (Math.abs(pos) > contentWidth) pos += Math.sign(pos) * contentWidth;
      wrapper.style.transform = `translate3d(${pos}px,0,0)`;
      this.#rafId = requestAnimationFrame(step);
    };

    this.#rafId = requestAnimationFrame(step);
  }

  #stopRAF() {
    this.#rafRunning = false;
    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #shouldUseRAFFallback() {
    const ua = navigator.userAgent;
    const isiOS = /\b(iPad|iPhone|iPod)\b/.test(ua) && !window.MSStream;
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    return isiOS && isTouch;
  }
}

// animateValue (unchanged)
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  function animate(currentTime) {
    if (cancelled) return;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const eased = easing(progress);
    currentValue = from + (to - from) * eased;
    onUpdate(currentValue);
    if (progress < 1) requestAnimationFrame(animate);
    else if (typeof onComplete === 'function') onComplete();
  }

  requestAnimationFrame(animate);

  return {
    get current() { return currentValue; },
    cancel() { cancelled = true; },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
